import React from 'react';
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
} from '@mui/material';

import { ActivityCandidate } from './activityplanning.models';

export type ActivityFormMode = 'ADD' | 'EDIT';

export type ActivityFormProps = {
  mode: ActivityFormMode;

  planningCycleName: string;
  budgetPeriodName: string;
  costCenterCode: string;

  activityCandidates: ActivityCandidate[];
  selectedActivity: ActivityCandidate | null;

  activityWeight: number;
  driverValue: number;
  targetValue: number;
  activityDescription: string;

  saving?: boolean;
  disabled?: boolean;

  onActivitySelect?: (value: ActivityCandidate | null) => void;
  onFieldChange: (name: string, value: any) => void;

  onSave: () => void;
  onCancel?: () => void;
};

export default class ActivityForm extends React.PureComponent<ActivityFormProps> {
  render() {
    const p = this.props;

    const editing = p.mode === 'EDIT';
    const disabled = Boolean(p.disabled || p.saving);

    const canSave =
      (editing || Boolean(p.selectedActivity)) &&
      Boolean(p.activityDescription?.trim()) &&
      !disabled;

    return (
      <Stack spacing={1.5}>
        {!editing && (

          <>
        <TextField
          size="small"
          label="Planning Cycle"
          value={p.planningCycleName}
          disabled
        />

        <TextField
          size="small"
          label="Budget Period"
          value={p.budgetPeriodName}
          disabled
        />

        <TextField
          size="small"
          label="Cost Center"
          value={p.costCenterCode}
          disabled
        />

        <Autocomplete
          size="small"
          options={p.activityCandidates}
          value={p.selectedActivity}
          onChange={(_, value) => p.onActivitySelect?.(value)}
          getOptionLabel={(x) => `${x.shc} — ${x.nam}`}
          isOptionEqualToValue={(a, b) => a.rid === b.rid}
          disabled={editing || disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Activity"
              required={!editing}
              helperText={
                editing
                  ? 'Activity identity cannot be changed after it has been added to the plan.'
                  : 'Select an entitled Activity for the selected Indicator.'
              }
            />
          )}
        />

        </>

        )}

        <TextField
          size="small"
          label="Indicator Contribution Weight"
          type="number"
          value={p.activityWeight}
          disabled={disabled}
          onChange={(e) =>
            p.onFieldChange(
              'activityWeight',
              Number(e.target.value || 0)
            )
          }
        />

        <TextField
          size="small"
          label="Driver Value"
          type="number"
          value={p.driverValue}
          disabled={disabled}
          onChange={(e) =>
            p.onFieldChange(
              'driverValue',
              Number(e.target.value || 0)
            )
          }
        />

        <TextField
          size="small"
          label="Target Value"
          type="number"
          value={p.targetValue}
          disabled={disabled}
          onChange={(e) =>
            p.onFieldChange(
              'targetValue',
              Number(e.target.value || 0)
            )
          }
        />

        <TextField
          size="small"
          label="Expected Result / Planning Notes"
          value={p.activityDescription}
          disabled={disabled}
          onChange={(e) =>
            p.onFieldChange(
              'activityDescription',
              e.target.value
            )
          }
          multiline
          minRows={2}
          required
        />

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
        >
          {p.onCancel && (
            <Button
              onClick={p.onCancel}
              disabled={Boolean(p.saving)}
            >
              Cancel
            </Button>
          )}

          <Button
            variant="contained"
            onClick={p.onSave}
            disabled={!canSave}
          >
            {editing
              ? 'Save Activity Changes'
              : 'Add Activity'}
          </Button>
        </Stack>
      </Stack>
    );
  }
}